import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import ItemThumbnail from 'components/lookBoardThumbnails/ItemThumbnail/ItemThumbnail';
import SpriteIcon from 'components/ui/SpriteIcon';
import IconButton from 'components/ui/IconButton/IconButton';
import classes from 'components/lookBoardThumbnails/LookBoard/LookBoard.module.scss';
import votedIcon from 'assets/icons/voted.svg';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { maxWidthMd } from '../../../constants/mediaQueries';

const LookBoardPreview = ({
  className,
  title,
  color,
  isLiked,
  isVoted,
  banner,
  isOnSlider,
  products,
  showStaticOverlay,
  staticOverlay,
  hoverOverlay,
  showLike,
  disableHover,
  children,
  shortTitle,
  onToggleLike,
  moveToDetails,
  onVoteClick,
  isGalleryView,
  isPreviewModal,
}) => {
  const matchesMediaQuery = useMediaQuery(maxWidthMd);

  const containerRef = useRef(null);
  const [imgHeight, setImgHeight] = useState('50%');
  const [containerHeight, setContainerHeight] = useState(null);
  const [productList, setProductList] = useState([]);
  const [hover, setHover] = useState(false);

  const hiddenItemsCount = useMemo(
    () => products.length - productList.length,
    [products, productList]
  );

  const updateHeight = useCallback(() => {
    if (containerRef.current) {
      const refWidth = containerRef.current.clientWidth;
      const itemHeight =
        products.length > 1 && !isPreviewModal
          ? Math.round(refWidth / 2)
          : refWidth;
      setContainerHeight(refWidth);
      setImgHeight(itemHeight);
    }
  }, [isPreviewModal, products.length]);

  useEffect(() => {
    updateHeight();
    // eslint-disable-next-line
  }, [products]);

  useEffect(() => {
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const list = products.slice(0, 4);
    switch (list.length) {
      case 2: {
        list.splice(1, 0, { id: uuidv4() }, { id: uuidv4() });
        break;
      }
      case 3: {
        list.push({ id: uuidv4() });
        break;
      }
      default: {
        break;
      }
    }
    setProductList(list);
  }, [products]);

  const handleHover = useCallback(() => {
    if (!disableHover) {
      setHover(true);
    }
  }, [disableHover]);

  const handleBlur = useCallback(() => {
    setHover(false);
  }, []);

  const bannerClass = useMemo(() => {
    switch (banner) {
      case 'Best Match':
        return classes.match;
      case 'Designer`s Choice':
        return classes.choise;
      case 'Most Stylish':
        return classes.likes;
      default:
        return '';
    }
  }, [banner]);

  return (
    <div
      ref={containerRef}
      className={clsx(classes.root, className)}
      onMouseEnter={!matchesMediaQuery ? handleHover : null}
      onMouseLeave={!matchesMediaQuery ? handleBlur : null}
    >
      <div
        className={clsx(classes.title, { [classes.shortTitle]: shortTitle })}
        style={{ backgroundColor: color }}
      >
        {title}
      </div>
      <div
        style={{ minHeight: containerHeight }}
        className={clsx(classes.imgContainer)}
        onClick={moveToDetails}
      >
        {productList.map(({ id: productId, media, itemClassId, slug }) =>
          media ? (
            <ItemThumbnail
              key={productId}
              id={productId}
              slug={slug}
              src={media}
              className={products.length > 1 ? classes.double : classes.single}
              itemClassId={itemClassId}
              height={imgHeight}
            />
          ) : (
            <div
              key={productId}
              className={clsx(classes.placeholderWrapper, classes.double)}
              style={{ height: imgHeight }}
            >
              <div className={classes.placeholder}>
                <SpriteIcon name="logo" className={classes.placeholderIcon} />
              </div>
            </div>
          )
        )}
      </div>
      <div
        className={clsx(
          classes.bottomControls,
          !isGalleryView && classes.outCardControls,
          !showLike && 'd-none'
        )}
      >
        {hiddenItemsCount > 0 && (
          <IconButton
            variant="inverted-grey"
            color="primary"
            size="sm"
            className="mr-1"
          >
            +{hiddenItemsCount}
          </IconButton>
        )}
        {showLike && (
          <>
            <IconButton
              className="mr-1"
              variant="inverted-grey"
              color="primary"
              size="sm"
              onClick={onToggleLike}
            >
              <SpriteIcon name={isLiked ? 'like-filled' : 'like'} size="sm" />
            </IconButton>
            {isVoted ? (
              <div className="cursor-pointer" style={{ width: '32px' }}>
                <img src={votedIcon} alt="voted" />
              </div>
            ) : (
              <IconButton
                variant="inverted-grey"
                size="sm"
                className={classes.vote}
                onClick={onVoteClick}
              >
                <span>Vote</span>
              </IconButton>
            )}
          </>
        )}
      </div>
      <>
        {showStaticOverlay && (
          <div className={classes.staticOverlay}>{staticOverlay}</div>
        )}
        <div
          className={clsx(classes.overlay, {
            [classes.hidden]: !hover && !matchesMediaQuery,
            [classes.mobileHover]: matchesMediaQuery,
          })}
        >
          {hoverOverlay}
        </div>
      </>
      {banner && (
        <div
          className={clsx(classes.banner, bannerClass, {
            [classes.hidden]: hover && !matchesMediaQuery,
          })}
        >
          <span>
            {banner === 'Best Match' && (
              <svg
                width="15"
                height="13"
                viewBox="0 0 15 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.0026 4.33332C13.3582 4.33332 13.6693 4.46665 13.9359 4.73332C14.2026 4.99998 14.3359 5.31109 14.3359 5.66665V6.99998C14.3359 7.07776 14.3248 7.16109 14.3026 7.24998C14.2804 7.33887 14.2582 7.42221 14.2359 7.49998L12.2359 12.2C12.1359 12.4222 11.9693 12.6111 11.7359 12.7666C11.5026 12.9222 11.2582 13 11.0026 13H3.66927V4.33332L7.66927 0.36665C7.83594 0.199984 8.03316 0.102761 8.26094 0.0749837C8.48872 0.0472059 8.70816 0.0888726 8.91927 0.199984C9.13038 0.311095 9.28594 0.46665 9.38594 0.66665C9.48594 0.86665 9.50816 1.07221 9.4526 1.28332L8.7026 4.33332H13.0026ZM5.0026 4.89998V11.6666H11.0026L13.0026 6.99998V5.66665H7.0026L7.9026 1.99998L5.0026 4.89998ZM1.66927 13C1.3026 13 0.988715 12.8694 0.727604 12.6083C0.466493 12.3472 0.335938 12.0333 0.335938 11.6666V5.66665C0.335938 5.29998 0.466493 4.98609 0.727604 4.72498C0.988715 4.46387 1.3026 4.33332 1.66927 4.33332H3.66927V5.66665H1.66927V11.6666H3.66927V13H1.66927Z"
                  fill="white"
                />
              </svg>
            )}
            {banner === 'Most Stylish' && (
              <svg
                width="14"
                height="13"
                viewBox="0 0 14 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.0026 12.55C6.84705 12.55 6.68871 12.5222 6.5276 12.4667C6.36649 12.4111 6.22483 12.3222 6.1026 12.2L4.9526 11.15C3.77483 10.0722 2.71094 9.00278 1.76094 7.94166C0.810937 6.88055 0.335938 5.71111 0.335938 4.43333C0.335938 3.38889 0.685937 2.51666 1.38594 1.81666C2.08594 1.11666 2.95816 0.766663 4.0026 0.766663C4.59149 0.766663 5.14705 0.891663 5.66927 1.14166C6.19149 1.39166 6.63594 1.73333 7.0026 2.16666C7.36927 1.73333 7.81371 1.39166 8.33594 1.14166C8.85816 0.891663 9.41371 0.766663 10.0026 0.766663C11.047 0.766663 11.9193 1.11666 12.6193 1.81666C13.3193 2.51666 13.6693 3.38889 13.6693 4.43333C13.6693 5.71111 13.197 6.88333 12.2526 7.95C11.3082 9.01666 10.2359 10.0889 9.03594 11.1667L7.9026 12.2C7.78038 12.3222 7.63871 12.4111 7.4776 12.4667C7.31649 12.5222 7.15816 12.55 7.0026 12.55ZM6.36927 3.5C6.04705 3.04444 5.7026 2.69722 5.33594 2.45833C4.96927 2.21944 4.52483 2.1 4.0026 2.1C3.33594 2.1 2.78038 2.32222 2.33594 2.76666C1.89149 3.21111 1.66927 3.76666 1.66927 4.43333C1.66927 5.01111 1.87483 5.625 2.28594 6.275C2.69705 6.925 3.18872 7.55555 3.76094 8.16666C4.33316 8.77777 4.92205 9.35 5.5276 9.88333C6.13316 10.4167 6.62483 10.8556 7.0026 11.2C7.38038 10.8556 7.87205 10.4167 8.4776 9.88333C9.08316 9.35 9.67205 8.77777 10.2443 8.16666C10.8165 7.55555 11.3082 6.925 11.7193 6.275C12.1304 5.625 12.3359 5.01111 12.3359 4.43333C12.3359 3.76666 12.1137 3.21111 11.6693 2.76666C11.2248 2.32222 10.6693 2.1 10.0026 2.1C9.48038 2.1 9.03594 2.21944 8.66927 2.45833C8.3026 2.69722 7.95816 3.04444 7.63594 3.5C7.55816 3.61111 7.46372 3.69444 7.3526 3.75C7.24149 3.80555 7.12483 3.83333 7.0026 3.83333C6.88038 3.83333 6.76371 3.80555 6.6526 3.75C6.54149 3.69444 6.44705 3.61111 6.36927 3.5Z"
                  fill="#D9CAB6"
                />
              </svg>
            )}
            {banner === "Designer's Choice" && (
              <svg
                width="14"
                height="12"
                viewBox="0 0 14 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.1302 7.66669L12.6635 5.50003L13.0969 5.53336C13.2858 5.55558 13.433 5.63336 13.5385 5.7667C13.6441 5.90003 13.6969 6.04447 13.6969 6.20003C13.6969 6.28892 13.6802 6.37781 13.6469 6.46669C13.6135 6.55558 13.5524 6.63336 13.4635 6.70003L11.7302 8.21669L12.2469 10.4667C12.258 10.4889 12.2635 10.5139 12.2635 10.5417V10.6167C12.2635 10.8056 12.1969 10.9639 12.0635 11.0917C11.9302 11.2195 11.7747 11.2834 11.5969 11.2834C11.5413 11.2834 11.483 11.275 11.4219 11.2584C11.3608 11.2417 11.3024 11.2167 11.2469 11.1834L10.8969 10.9667L10.1302 7.66669ZM8.56354 2.80003L7.86354 1.1667L8.01354 0.800028C8.0691 0.666695 8.15521 0.563917 8.27187 0.491695C8.38854 0.419473 8.50799 0.383362 8.63021 0.383362C8.75243 0.383362 8.87187 0.416695 8.98854 0.483362C9.10521 0.550028 9.19132 0.650028 9.24687 0.783362L10.1635 2.95003L8.56354 2.80003ZM3.89687 9.21669L5.99687 7.95003L8.09687 9.23336L7.54687 6.83336L9.39687 5.23336L6.96354 5.0167L5.99687 2.75003L5.03021 5.00003L2.59687 5.21669L4.44687 6.83336L3.89687 9.21669ZM2.23021 10.4667L2.96354 7.31669L0.513542 5.20003C0.424653 5.13336 0.366319 5.05558 0.338542 4.96669C0.310764 4.87781 0.296875 4.78892 0.296875 4.70003C0.296875 4.54447 0.349653 4.40003 0.455208 4.2667C0.560764 4.13336 0.707986 4.05558 0.896875 4.03336L4.13021 3.75003L5.38021 0.783362C5.43576 0.650028 5.52187 0.550028 5.63854 0.483362C5.75521 0.416695 5.87465 0.383362 5.99687 0.383362C6.1191 0.383362 6.23854 0.416695 6.35521 0.483362C6.47187 0.550028 6.55799 0.650028 6.61354 0.783362L7.86354 3.75003L11.0969 4.03336C11.2858 4.05558 11.433 4.13336 11.5385 4.2667C11.6441 4.40003 11.6969 4.54447 11.6969 4.70003C11.6969 4.78892 11.683 4.87781 11.6552 4.96669C11.6274 5.05558 11.5691 5.13336 11.4802 5.20003L9.03021 7.31669L9.76354 10.4667C9.77465 10.5 9.78021 10.55 9.78021 10.6167C9.78021 10.8056 9.71354 10.9639 9.58021 11.0917C9.44687 11.2195 9.29132 11.2834 9.11354 11.2834C9.08021 11.2834 8.96354 11.25 8.76354 11.1834L5.99687 9.5167L3.23021 11.1834C3.17465 11.2167 3.11632 11.2417 3.05521 11.2584C2.9941 11.275 2.93576 11.2834 2.88021 11.2834C2.68021 11.2834 2.50799 11.2028 2.36354 11.0417C2.2191 10.8806 2.17465 10.6889 2.23021 10.4667Z"
                  fill="#D9CAB6"
                />
              </svg>
            )}
            {banner}
          </span>
        </div>
      )}
      {children}
    </div>
  );
};

LookBoardPreview.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  isLiked: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  isVoted: PropTypes.bool,
  banner: PropTypes.string,
  isOnSlider: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  className: PropTypes.string,
  hoverOverlay: PropTypes.node,
  showStaticOverlay: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  disableHover: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  staticOverlay: PropTypes.node,
  showLike: PropTypes.bool,
  children: PropTypes.node,
  shortTitle: PropTypes.bool,
  onToggleLike: PropTypes.func,
  moveToDetails: PropTypes.func,
  onVoteClick: PropTypes.func,
  isGalleryView: PropTypes.bool,
  isPreviewModal: PropTypes.bool,
};

LookBoardPreview.defaultProps = {
  isLiked: 0,
  isVoted: false,
  banner: '',
  isOnSlider: false,
  className: '',
  showStaticOverlay: false,
  staticOverlay: null,
  showLike: true,
  children: null,
  disableHover: false,
  shortTitle: false,
  isGalleryView: false,
  onToggleLike: () => {},
  moveToDetails: () => {},
  onVoteClick: () => {},
  isPreviewModal: false,
  hoverOverlay: null,
};
export default LookBoardPreview;
